/**
 * API service for customer operations
 * Follows the same pattern as invoiceService
 */

import { getConfig } from './config';
import { authenticatedFetch } from './authService';

/**
 * Register a new customer
 * @param {Object} customerData - Customer information
 * @returns {Promise<Object>} Customer registration result
 */
export const createCustomer = async (customerData) => {
    const config = getConfig();
    const API_BASE_URL = config.apiBaseUrl;
    
    // Log API request for debugging
    console.log(`API Request to: ${API_BASE_URL}/clientes`);
    console.log('Customer data:', JSON.stringify(customerData, null, 2));
    
    try {
        // Prepare the request payload according to API documentation
        const requestPayload = {
            rfc: customerData.rfc,
            razon_social: customerData.razonSocial,
            email: customerData.email || '',
            telefono: customerData.telefono || '',
            cp_fiscal: customerData.codigoPostal,
            uso_cfdi_clave: customerData.usoCfdi,
            regimen_fiscal_clave: customerData.regimenFiscal,
            // Include sale ID and sucursal ID if available
            venta_id: customerData.ventaId || null,
            sucursal_id: customerData.sucursalId || null
        };
        
        console.log('Request payload:', JSON.stringify(requestPayload, null, 2));
        
        const response = await authenticatedFetch(`${API_BASE_URL}/clientes`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestPayload)
        });
        
        // Handle different response status codes
        if (response.status === 201) {
            const data = await response.json();
            console.log('API Response from POST /clientes:', JSON.stringify(data, null, 2));
            
            // Extract cliente_id from the response structure:
            // { "message": "Cliente creado exitosamente", "cliente": { "success": true, "cliente_id": 29623 } }
            const clienteId = data.cliente || 0;
            
            console.log('Extracted cliente_id:', clienteId);
            
            // Transform API response to match expected customer format for the app
            const customerResult = {
                id: clienteId,
                rfc: data.cliente?.rfc || customerData.rfc,
                razonSocial: data.cliente?.razon_social || customerData.razonSocial,
                email: data.cliente?.email || customerData.email,
                telefono: data.cliente?.telefono || customerData.telefono,
                codigoPostal: data.cliente?.cp_fiscal || customerData.codigoPostal,
                usoCfdi: data.cliente?.uso_cfdi_clave || customerData.usoCfdi,
                regimenFiscal: data.cliente?.regimen_fiscal_clave || customerData.regimenFiscal
            };
            
            return {
                success: true,
                data: customerResult,
                cliente: data.cliente // Pass through the raw cliente object from the response
            };
        }
        
        if (response.status === 400) {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'Error de validación en los datos del cliente',
                detailedError: errorData.error || null
            };
        }
        
        if (response.status === 409) {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'El cliente ya está registrado',
                isDuplicate: true
            };
        }
        
        if (response.status === 500) {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'Error interno del servidor. Por favor contacte soporte.',
                detailedError: errorData.error || null
            };
        }
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            return {
                success: false,
                error: errorData?.message || `Error al registrar cliente: ${response.status} ${response.statusText}`,
                detailedError: errorData?.error || null
            };
        }
        
        const data = await response.json();
        console.log('API Response from POST /clientes:', JSON.stringify(data, null, 2));
        
        return {
            success: true,
            data: data
        };
        
    } catch (error) {
        console.error('Error creating customer:', error);
        
        // Handle network errors
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            return {
                success: false,
                error: 'No se pudo conectar con el servidor. Verifique su conexión a internet.'
            };
        }
        
        return {
            success: false,
            error: error.message || 'Error inesperado al registrar el cliente'
        };
    }
};

/**
 * Update an existing customer
 * @param {Object} customerData - Customer information including ID and originalRFC
 * @returns {Promise<Object>} Customer update result
 */
export const updateCustomer = async (customerData) => {
    const config = getConfig();
    const API_BASE_URL = config.apiBaseUrl;
    
    // Log API request for debugging
    console.log(`API Request to: ${API_BASE_URL}/clientes/${customerData.id}`);
    console.log('Customer data:', JSON.stringify(customerData, null, 2));
    
    try {
        // Get the original RFC (fallback to current RFC if not provided)
        const originalRFC = customerData.originalRFC || customerData.rfc;
        
        // Prepare the request payload according to API documentation
        // rfc = original RFC, rfc_nuevo = new RFC (or same as original if unchanged)
        const requestPayload = {
            rfc: originalRFC,
            rfc_nuevo: customerData.rfc,
            razon_social: customerData.razonSocial,
            email: customerData.email || '',
            telefono: customerData.telefono || '',
            cp_fiscal: customerData.codigoPostal,
            uso_cfdi_clave: customerData.usoCfdi,
            regimen_fiscal_clave: customerData.regimenFiscal
        };
        
        console.log('Request payload:', JSON.stringify(requestPayload, null, 2));
        
        const response = await authenticatedFetch(`${API_BASE_URL}/clientes/${customerData.id}`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestPayload)
        });
        
        // Handle different response status codes
        if (response.status === 200) {
            const data = await response.json();
            console.log('API Response from PUT /clientes:', JSON.stringify(data, null, 2));
            
            // Transform API response to match expected customer format for the app
            const customerResult = {
                id: customerData.id,
                rfc: data.cliente?.rfc || customerData.rfc,
                razonSocial: data.cliente?.razon_social || customerData.razonSocial,
                email: data.cliente?.email || customerData.email,
                telefono: data.cliente?.telefono || customerData.telefono,
                codigoPostal: data.cliente?.cp_fiscal || customerData.codigoPostal,
                usoCfdi: data.cliente?.uso_cfdi_clave || customerData.usoCfdi,
                regimenFiscal: data.cliente?.regimen_fiscal_clave || customerData.regimenFiscal
            };
            
            return {
                success: true,
                data: customerResult
            };
        }
        
        if (response.status === 400) {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'Error de validación en los datos del cliente',
                detailedError: errorData.error || null
            };
        }
        
        if (response.status === 404) {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'Cliente no encontrado',
                notFound: true
            };
        }
        
        if (response.status === 500) {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'Error interno del servidor. Por favor contacte soporte.',
                detailedError: errorData.error || null
            };
        }
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            return {
                success: false,
                error: errorData?.message || `Error al actualizar cliente: ${response.status} ${response.statusText}`,
                detailedError: errorData?.error || null
            };
        }
        
        const data = await response.json();
        console.log('API Response from PUT /clientes:', JSON.stringify(data, null, 2));
        
        return {
            success: true,
            data: data
        };
        
    } catch (error) {
        console.error('Error updating customer:', error);
        
        // Handle network errors
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            return {
                success: false,
                error: 'No se pudo conectar con el servidor. Verifique su conexión a internet.'
            };
        }
        
        return {
            success: false,
            error: error.message || 'Error inesperado al actualizar el cliente'
        };
    }
};
